import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import '../../styles/sidebar.css';
import {
  FaTachometerAlt,
  FaAngleRight,
  FaUserShield,
  FaUser,
  FaAngleDown,
  FaUsers,
  FaMapMarkerAlt,
  FaUserGraduate,
  FaSchool,
  FaClipboardList,
  FaClipboardCheck,
  FaBook,
  FaRocket,
  FaFileInvoice,
  FaFilePdf,
  FaIndustry,
  FaChartBar,
  FaQrcode,
  FaTruck,
  FaMoneyBillWave,
  FaSignOutAlt,
  FaDatabase,
  FaShieldAlt,
} from 'react-icons/fa';
import { FaGraduationCap as FaGradCap } from 'react-icons/fa';
import stbbLogo from '../../uploads/stbb_logo.png';
import { useApp } from '../../context/AppContext';

const hasPerm = (perms, userPerms, isSuper) => {
  if (isSuper) return true;
  if (!perms || perms.length === 0) return true;
  return perms.some(p => userPerms.includes(p));
};

// Section-grouped navigation. Section records keep the config readable but are not rendered.
const commonLinks = [
  { type: 'item', icon: FaTachometerAlt, text: 'Dashboard', page: 'dashboard' },

  { type: 'section', label: 'Administration' },
  { type: 'item', icon: FaUsers, text: 'Administration', hasSubmenu: true, submenu: [
    { icon: FaUser,       text: 'Users',            page: 'user-management',  perms: ['VIEW_USERS'] },
    { icon: FaUserShield, text: 'Roles',             page: 'roles',            perms: ['VIEW_ROLES'] },
    { icon: FaUserShield, text: 'Permissions',       page: 'permissions',      perms: ['VIEW_PERMISSIONS'] },
    { icon: FaUserShield, text: 'Role Permissions',  page: 'role-permissions', perms: ['EDIT_ROLES'] },
    { icon: FaClipboardList, text: 'Audit Logs',     page: 'audit-logs',       perms: ['VIEW_AUDIT_LOGS'] },
    { icon: FaShieldAlt,     text: 'Event Timeline', page: 'operational-events', perms: ['VIEW_OPERATIONAL_EVENTS'] },
    { icon: FaDatabase,   text: 'DB Backups',       page: 'backups',          perms: ['VIEW_USERS'] },
  ]},

  { type: 'section', label: 'Institutions' },
  { type: 'item', icon: FaMapMarkerAlt, text: 'Institutions', hasSubmenu: true, submenu: [
    { icon: FaMapMarkerAlt, text: 'Regions',   page: 'region-management',   perms: ['VIEW_REGIONS'] },
    { icon: FaMapMarkerAlt, text: 'Districts', page: 'district-management', perms: ['VIEW_DISTRICTS'] },
    { icon: FaMapMarkerAlt, text: 'Talukas',   page: 'taluka-management',   perms: ['VIEW_TALUKAS'] },
    { icon: FaSchool,       text: 'Schools',   page: 'school-management',   perms: ['VIEW_SCHOOLS'] },
    { icon: FaUser,         text: 'Head Masters', page: 'head-masters',     perms: ['VIEW_USERS'] },
  ]},

  { type: 'section', label: 'Academics' },
  { type: 'item', icon: FaBook, text: 'Academics', hasSubmenu: true, submenu: [
    { icon: FaBook,          text: 'Books',    page: 'books',              perms: ['VIEW_BOOKS'] },
    { icon: FaClipboardCheck,text: 'Courses',  page: 'courses',            perms: ['VIEW_COURSES'] },
    { icon: FaGradCap,       text: 'Classes',  page: 'classes',            perms: ['VIEW_CLASSES'] },
    { icon: FaRocket,        text: 'Projects', page: 'projects',           perms: ['VIEW_PROJECTS'] },
    { icon: FaRocket,        text: 'Packages', page: 'package-management', perms: ['VIEW_PACKAGES'] },
  ]},

  { type: 'section', label: 'Enrollment' },
  { type: 'item', icon: FaUserGraduate, text: 'Enrollment', hasSubmenu: true, submenu: [
    { icon: FaUserGraduate, text: 'Students',             page: 'student-management',   perms: ['VIEW_STUDENTS'] },
  ]},

  { type: 'section', label: 'Addons' },
  {
    type: 'item',
    icon: FaClipboardCheck,
    text: 'Enrollment Collection',
    page: 'collected-enrollment',
  },

  { type: 'section', label: 'Procurement' },
  { type: 'item', icon: FaFileInvoice, text: 'Procurement', hasSubmenu: true, submenu: [
    { icon: FaFileInvoice,   text: 'Tenders',     page: 'tenders',     perms: ['VIEW_TENDERS'] },
    { icon: FaClipboardList, text: 'Work Orders', page: 'work-orders', perms: ['VIEW_WORK_ORDERS'] },
    { icon: FaIndustry,      text: 'Publishers',  page: 'publishers',  perms: ['VIEW_publisherS'] },
  ]},

  { type: 'section', label: 'Inventory' },
  { type: 'item', icon: FaIndustry, text: 'Inventory', hasSubmenu: true, submenu: [
    { icon: FaClipboardList,  text: 'Dispatch Notes',    page: 'dispatch-notes',     perms: ['VIEW_DISPATCH_NOTES'] },
    { icon: FaClipboardList,  text: 'Create Dispatch',   page: 'dispatch-create',    perms: ['CREATE_DISPATCH_NOTES'] },
    { icon: FaClipboardList,  text: 'Incoming Shipments',page: 'incoming-shipments', perms: ['VIEW_DISPATCH_NOTES'] },
    { icon: FaClipboardList,  text: 'Gate Scan',         page: 'gate-scan',          perms: ['SCAN_GATE_INWARD'] },
    { icon: FaClipboardCheck, text: 'QC Receipt',        page: 'qc-receipt',         perms: ['ISSUE_FRN'] },
    { icon: FaIndustry,       text: 'Warehouses',        page: 'warehouses',         perms: ['VIEW_WAREHOUSES'] },
    { icon: FaClipboardList,  text: 'Bundles',           page: 'bundles',            perms: ['VIEW_BUNDLES'] },
    { icon: FaClipboardList,  text: 'Receipts',          page: 'receipts',           perms: ['VIEW_RECEIPTS'] },
    { icon: FaClipboardList,  text: 'Issues',            page: 'issues',             perms: ['VIEW_ISSUES'] },
    { icon: FaMapMarkerAlt,   text: 'Inventory Heatmap',  page: 'inventory-heatmap', perms: ['VIEW_WAREHOUSES'] },
  ]},

  { type: 'section', label: 'Finance & Reports' },
  { type: 'item', icon: FaMoneyBillWave, text: 'Finance', hasSubmenu: true, submenu: [
    { icon: FaMoneyBillWave, text: 'Dashboard', page: 'finance-dashboard', perms: ['VIEW_INVOICES'] },
    { icon: FaFileInvoice,   text: 'Invoices',  page: 'finance-invoices',  perms: ['VIEW_INVOICES'] },
    { icon: FaClipboardList, text: 'Bills',     page: 'finance-bills',     perms: ['VIEW_BILLS'] },
    { icon: FaFilePdf,       text: 'Vouchers',  page: 'finance-vouchers',  perms: ['VIEW_VOUCHERS'] },
  ]},
  { type: 'item', icon: FaChartBar, text: 'Reports', hasSubmenu: true, submenu: [
    { icon: FaChartBar, text: 'Enrollment',  page: 'reports-enrollment',  perms: ['VIEW_REPORTS'] },
    { icon: FaChartBar, text: 'Procurement', page: 'reports-procurement', perms: ['VIEW_REPORTS'] },
  ]},
];

const publisherLinks = [
  { type: 'item', icon: FaClipboardList,  text: 'Dashboard',      page: 'publisher-dashboard' },
  { type: 'item', icon: FaClipboardCheck, text: 'Work Orders',    page: 'publisher-workorders' },
  { type: 'item', icon: FaUser,           text: 'Profile',        page: 'publisher-profile' },
  { type: 'item', icon: FaClipboardList,  text: 'Dispatch Notes', page: 'publisher-dispatch-notes' },
  { type: 'item', icon: FaClipboardList,  text: 'Create Dispatch',page: 'publisher-dispatch-create' },
  { type: 'item', icon: FaFileInvoice,    text: 'Tenders',        page: 'publisher-tenders' },
  { type: 'item', icon: FaClipboardList,  text: 'Tickets',        page: 'publisher-tickets' },
];

const qcLinks = [
  { type: 'item', icon: FaClipboardList,  text: 'Dashboard',          page: 'qc-dashboard' },
  { type: 'item', icon: FaClipboardCheck, text: 'Pending Receipts',   page: 'qc-receipt' },
  { type: 'item', icon: FaClipboardList,  text: 'Completed Receipts', page: 'qc-final-receipts' },
];

const storeOfficerLinks = [
  { type: 'item', icon: FaClipboardList, text: 'Dashboard', page: 'store-dashboard' },
  { type: 'item', icon: FaClipboardList, text: 'Issues',    page: 'issues' },
  { type: 'item', icon: FaClipboardList, text: 'Receipts',  page: 'receipts' },
];

const gateOfficerLinks = [
  { type: 'item', icon: FaTachometerAlt,  text: 'Dashboard',          page: 'gate-dashboard' },
  { type: 'item', icon: FaQrcode,         text: 'Gate Scan',          page: 'gate-scan' },
  { type: 'item', icon: FaTruck,          text: 'Incoming Shipments', page: 'incoming-shipments' },
  { type: 'item', icon: FaClipboardCheck, text: 'Provisional Receipts',page: 'provisional-receipts' },
];

const financeLinks = [
  { type: 'item', icon: FaMoneyBillWave, text: 'Dashboard', page: 'finance-dashboard' },
  { type: 'item', icon: FaFileInvoice,   text: 'Invoices',  page: 'finance-invoices' },
  { type: 'item', icon: FaClipboardList, text: 'Bills',     page: 'finance-bills' },
  { type: 'item', icon: FaFilePdf,       text: 'Vouchers',  page: 'finance-vouchers' },
];

const headMasterLinks = [
  { type: 'item', icon: FaTachometerAlt, text: 'Dashboard', page: 'head-master-dashboard' },
  { type: 'item', icon: FaUserGraduate, text: 'Students', page: 'head-master-students' },
  { type: 'item', icon: FaClipboardCheck, text: 'Manual Enrollment', page: 'head-master-enrollment' },
];

const procurementLinks = [
  { type: 'item', icon: FaTachometerAlt, text: 'Dashboard', page: 'procurement-dashboard' },
  { type: 'item', icon: FaUserGraduate, text: 'Enrollment Demand', page: 'procurement-enrollment' },
  { type: 'item', icon: FaFileInvoice, text: 'Tenders', page: 'tenders' },
  { type: 'item', icon: FaIndustry, text: 'Publishers', page: 'publishers' },
  { type: 'item', icon: FaClipboardList, text: 'Work Orders', page: 'work-orders' },
];

const teoLinks = [
  { type: 'item', icon: FaTachometerAlt, text: 'Dashboard', page: 'dashboard' },
  { type: 'item', icon: FaUserGraduate, text: 'Enrollment', page: 'collected-enrollment' },
  { type: 'item', icon: FaSchool, text: 'Schools', page: 'school-management' },
  { type: 'item', icon: FaClipboardList, text: 'Warehouse Requests', page: 'teo-requisitions' },
  { type: 'item', icon: FaClipboardCheck, text: 'Head Master Handovers', page: 'teo-handovers' },
];

const ministerLinks = [
  { type: 'item', icon: FaTachometerAlt, text: 'Dashboard', page: 'minister-dashboard' },
  { type: 'item', icon: FaChartBar, text: 'Enrollment', page: 'reports-enrollment' },
  { type: 'item', icon: FaFileInvoice, text: 'Procurement', page: 'reports-procurement' },
];

const routeMap = {
  dashboard: '/dashboard',
  'finance-dashboard': '/finance/dashboard',
  'finance-invoices': '/finance/invoices',
  'finance-bills': '/finance/bills',
  'finance-vouchers': '/finance/vouchers',
  'publisher-dashboard': '/publisher/dashboard',
  'head-master-dashboard': '/hm/dashboard',
  'head-master-students': '/hm/students',
  'head-master-enrollment': '/hm/enrollment',
  'procurement-dashboard': '/procurement/dashboard',
  'procurement-enrollment': '/procurement/enrollment',
  'minister-dashboard': '/minister/dashboard',
  'teo-requisitions': '/teo/requisitions',
  'teo-handovers': '/teo/handovers',
  'qc-dashboard': '/qc/dashboard',
  'store-dashboard': '/so/dashboard',
  'gate-dashboard': '/go/dashboard',
  'user-management': '/users',
  roles: '/roles',
  permissions: '/permissions',
  'role-permissions': '/role-permissions',
  backups: '/backups',
  'region-management': '/regions',
  'taluka-management': '/taluka-management',
  'district-management': '/districts',
  'student-management': '/students',
  'school-management': '/schools',
  'head-masters': '/head-masters',
  'collected-enrollment': '/collected-enrollment',
  books: '/books',
  courses: '/courses',
  classes: '/classes',
  projects: '/projects',
  tenders: '/tenders',
  'work-orders': '/work-orders',
  publishers: '/publishers',
  warehouses: '/warehouses',
  bundles: '/bundles',
  receipts: '/so/receipts',
  issues: '/so/issues',
  'package-management': '/packages',
  'publisher-profile': '/publishers/profile',
  'publisher-workorders': '/publisher/work-orders',
  workorder: '/work-orders',
  'audit-logs': '/audit-logs',
  'operational-events': '/operational-events',
  'reports-enrollment': '/reports/enrollment',
  'reports-procurement': '/reports/procurement',
  'dispatch-notes': '/dispatch-notes',
  'dispatch-create': '/dispatch-notes/new',
  'incoming-shipments': '/go/incoming-shipments',
  'provisional-receipts': '/go/provisional-receipts',
  'gate-scan': '/go/gate-scan',
  'qc-receipt': '/qc/qc-receipt',
  'qc-final-receipts': '/qc/final-receipts',
  'publisher-dispatch-notes': '/publisher/dispatch-notes',
  'publisher-tenders': '/publisher/tenders',
  'publisher-tickets': '/publisher/tickets',
  'publisher-dispatch-create': '/publisher/dispatch-create',
  'inventory-heatmap': '/inventory/heatmap',
};

function Sidebar({ open = true }) {
  const { permissions, roles, user, logout } = useApp();
  const isSuper = roles?.includes('ROLE_SUPER_ADMIN');
  const [openSubmenus, setOpenSubmenus] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  const isPublisherRole     = roles?.includes('ROLE_PUBLISHER');
  const isQualityControlRole= roles?.includes('ROLE_QC') || roles?.includes('ROLE_QUALITY_CONTROL');
  const isStoreOfficerRole  = roles?.includes('ROLE_STORE_OFFICER') || roles?.includes('ROLE_STORE');
  const isFinanceRole       = roles?.includes('ROLE_ACCOUNTS_EXECUTIVE') || roles?.includes('ROLE_ACCOUNTS_OFFICER');
  const isGateOfficerRole   = roles?.includes('ROLE_GATE_OFFICER');
  const isHeadMasterRole    = roles?.includes('ROLE_HEAD_MASTER');
  const isProcurementRole   = roles?.includes('ROLE_PO') || roles?.includes('ROLE_PEO') || roles?.includes('ROLE_PROCUREMENT_OFFICER');
  const isTeoRole           = roles?.includes('ROLE_TO') || roles?.includes('ROLE_TEO');
  const isMinisterRole      = roles?.includes('ROLE_MINISTER');

  let linksSource = commonLinks;
  if (isPublisherRole)      linksSource = publisherLinks;
  else if (isMinisterRole) linksSource = ministerLinks;
  else if (isHeadMasterRole) linksSource = headMasterLinks;
  else if (isTeoRole) linksSource = teoLinks;
  else if (isProcurementRole) linksSource = procurementLinks;
  else if (isFinanceRole)   linksSource = financeLinks;
  else if (isQualityControlRole) linksSource = qcLinks;
  else if (isStoreOfficerRole)   linksSource = storeOfficerLinks;
  else if (isGateOfficerRole)    linksSource = gateOfficerLinks;

  const isStreamlinedRole = isPublisherRole || isMinisterRole || isHeadMasterRole || isTeoRole || isProcurementRole || isQualityControlRole || isStoreOfficerRole || isGateOfficerRole || isFinanceRole;

  const filteredLinks = isStreamlinedRole
    ? linksSource
    : linksSource.map(link => {
        if (link.type === 'section') return link;
        if (link.hasSubmenu) {
          const filteredSubmenu = link.submenu.filter(s => hasPerm(s.perms, permissions, isSuper));
          return { ...link, submenu: filteredSubmenu };
        }
        return link;
      }).filter(link => {
        if (link.type === 'section') return true;
        if (link.hasSubmenu) return link.submenu.length > 0;
        return hasPerm(link.perms, permissions, isSuper);
      });

  // Remove trailing section headers (section with nothing after it)
  const visibleLinks = filteredLinks.filter((link, i) => {
    if (link.type !== 'section') return true;
    const next = filteredLinks[i + 1];
    return next && next.type !== 'section';
  });

  // Auto-open submenu containing active route
  useEffect(() => {
    visibleLinks.forEach((l, i) => {
      if (l.type === 'item' && l.hasSubmenu) {
        const childActive = l.submenu.some(s => routeMap[s.page] && location.pathname.startsWith(routeMap[s.page]));
        if (childActive && !openSubmenus[i]) {
          setOpenSubmenus(p => ({ ...p, [i]: true }));
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleSubmenu = index => setOpenSubmenus(prev => ({ ...prev, [index]: !prev[index] }));

  const isItemActive = link => {
    if (!link.page || !routeMap[link.page]) return false;
    return location.pathname === routeMap[link.page] || location.pathname.startsWith(`${routeMap[link.page]}/`);
  };

  const isGroupActive = link => {
    if (!link.hasSubmenu) return false;
    return link.submenu.some(s => routeMap[s.page] && (location.pathname === routeMap[s.page] || location.pathname.startsWith(`${routeMap[s.page]}/`)));
  };

  const handleNav = (e, link, index) => {
    if (e.ctrlKey || e.metaKey || e.button === 1) return;
    e.preventDefault();
    if (link.hasSubmenu) {
      toggleSubmenu(index);
    } else if (link.page) {
      navigate(routeMap[link.page]);
    }
  };

  const handleSubNav = (e, sub) => {
    if (e.ctrlKey || e.metaKey || e.button === 1) return;
    e.preventDefault();
    if (sub.page) navigate(routeMap[sub.page]);
  };

  const avatarUrl = user?.avatar
    || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.username || 'U')}&background=059669&color=fff&size=64&rounded=true`;

  return (
    <aside className={`eduss-sidebar${open ? ' open' : ' closed'}`}>
      {/* Logo */}
      <div className="eduss-sidebar-logo">
        <div className="eduss-sidebar-logo-icon">
          <img src={stbbLogo} alt="STBB" />
        </div>
        <div className="eduss-sidebar-logo-text">
          <div className="eduss-sidebar-logo-title">STBB Portal</div>
          <div className="eduss-sidebar-logo-sub">Sindh Textbook Board</div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="eduss-sidebar-nav">
        {visibleLinks.map((link, i) => {
          if (link.type === 'section') {
            return null;
          }

          const active = isItemActive(link);
          const groupActive = isGroupActive(link);

          return (
            <div key={i} className="eduss-sidebar-nav-item-wrap">
              <Link
                to={link.page ? (routeMap[link.page] || '#') : '#'}
                title={!open ? link.text : undefined}
                onClick={e => handleNav(e, link, i)}
                className={`eduss-sidebar-nav-link${active || groupActive ? ' active' : ''}`}
              >
                <span className="eduss-sidebar-nav-icon">
                  <link.icon />
                </span>
                {open && <span className="eduss-sidebar-nav-text">{link.text}</span>}
                {link.hasSubmenu && open && (
                  <FaAngleDown className={`eduss-sidebar-nav-arrow${openSubmenus[i] ? ' open' : ''}`} />
                )}
                {link.hasSubmenu && !open && (
                  <FaAngleRight className="eduss-sidebar-nav-arrow-mini" />
                )}
              </Link>

              {link.hasSubmenu && openSubmenus[i] && open && (
                <div className="eduss-sidebar-submenu">
                  {link.submenu.map((sub, si) => (
                    <Link
                      key={si}
                      to={routeMap[sub.page] || '#'}
                      title={sub.text}
                      onClick={e => handleSubNav(e, sub)}
                      className={`eduss-sidebar-submenu-link${location.pathname === routeMap[sub.page] || location.pathname.startsWith(`${routeMap[sub.page]}/`) ? ' active' : ''}`}
                    >
                      <span className="eduss-sidebar-sub-icon"><sub.icon /></span>
                      <span>{sub.text}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Bottom user profile strip */}
      {open && user && (
        <div className="eduss-sidebar-user-strip">
          <img src={avatarUrl} alt={user.username} className="eduss-sidebar-user-avatar" />
          <div className="eduss-sidebar-user-info">
            <div className="eduss-sidebar-user-name">{user.username}</div>
            <div className="eduss-sidebar-user-role">
              {roles?.[0]?.replace('ROLE_', '') || 'User'}
            </div>
          </div>
          <button
            className="eduss-sidebar-signout-btn"
            onClick={() => logout?.()}
            title="Sign out"
          >
            <FaSignOutAlt />
          </button>
        </div>
      )}

      {!open && (
        <div className="eduss-sidebar-user-strip-mini">
          <img
            src={avatarUrl}
            alt={user?.username}
            className="eduss-sidebar-user-avatar"
            title={user?.username}
          />
        </div>
      )}
    </aside>
  );
}

Sidebar.propTypes = { open: PropTypes.bool };
export default Sidebar;
